import React, { useEffect, useMemo, useRef, useState } from "react";
import CustomTable from "../CustomTable";
import CustomButton from "../CustomButton";
import moment from "moment";
import DatePicker from "../FormFields/DatePicker";
import useApi from "../../hooks/useApi";
import { apiUrls } from "../../utils/api/apiUrls";
import { useAuth } from "../../contexts/AuthContext";
import { l1Columns, storeColumns } from "../Columns/Vouchers/AllVouchers";
import {
  downloadReports,
  downloadReportsFun,
  getArrayOfSelectedStoresCode,
} from "../../utils/helpers";
import {
  useNavigate,
  createSearchParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { toast } from "react-toastify";
import {
  normalizeVoucherParentRowForStoreColumns,
  resolveVoucherListRowId,
  storeVoucherParentsFromGetRecordsResponse,
} from "../../utils/dmsStandaloneListUtils";

/** Parse ?startDate / ?endDate without moment string heuristics (avoids deprecation warnings). */
function dateFromSearchParam(param, fallback) {
  if (param == null || String(param).trim() === "") {
    return fallback;
  }
  const d = new Date(param);
  return Number.isNaN(d.getTime()) ? fallback : d;
}

const AllVouchers = () => {
  // Do not default `data` to []: Store + missing storeGetResponse left data undefined → [] made Array.isArray(data) true and wiped the table.
  const { data, nonProcessedData, apiCall } = useApi();
  const { data: fileData, apiCall: fileDownload } = useApi();
  const { data: fileStoreData, apiCall: fileDownloadAtStore } = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const loc = useLocation();
  const [startDate, setStartDate] = useState(() =>
    dateFromSearchParam(
      searchParams.get("startDate"),
      moment().subtract(7, "days").toDate()
    )
  );

  const [endDate, setEndDate] = useState(() =>
    dateFromSearchParam(searchParams.get("endDate"), new Date())
  );
  const [tableData, setTableData] = useState();
  const {
    user: { userLabel = "Store", username },
    user,
    selectedStores,
    storeDropdownState,
    currentStoreSelect,
    setCurrentStoreSelect,
  } = useAuth();
  const getRecordsInFlightRef = useRef(false);
  const lastFetchKeyRef = useRef("");

  const { name: storeName } =
    selectedStores?.storeDetails || selectedStores?.[0] || {name:""};
  const selectedStoreCode =
    selectedStores?.storeDetails?.storeCode ||
    selectedStores?.storeCode ||
    selectedStores?.[0]?.storeCode ||
    "";

  const [selectedCheckBoxes, setSelectedCheckBoxes] = useState([]);

  let pathVar = loc?.pathname?.split("/") || "";
  let subVoucherPath = pathVar[pathVar.length - 1];

  // When Store uses useApi processData, `data` is a flat array — use nonProcessedData for nested shapes.
  const storeWiseRecords = (() => {
    const raw = Array.isArray(data) ? nonProcessedData : data;
    return raw?.storeWiseRecordsResponses ?? nonProcessedData?.storeWiseRecordsResponses;
  })();

  const storeListRowsFromApi = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) {
      return payload.map(normalizeVoucherParentRowForStoreColumns);
    }
    return storeVoucherParentsFromGetRecordsResponse(payload);
  };

  const storeFetchDependencyKey = useMemo(() => {
    const selectedCode =
      selectedStores?.storeDetails?.storeCode ||
      selectedStores?.storeCode ||
      selectedStores?.value ||
      "";
    const dropdownCount = Array.isArray(storeDropdownState)
      ? storeDropdownState.length
      : storeDropdownState
        ? 1
        : 0;
    return `${selectedCode}|${dropdownCount}`;
  }, [
    selectedStores?.storeDetails?.storeCode,
    selectedStores?.storeCode,
    selectedStores?.value,
    Array.isArray(storeDropdownState) ? storeDropdownState.length : 0,
  ]);

  const getStoreCode = (row) =>
    row?.storeCode || row?.store?.storeCode || row?.store?.code || "";

  const getStoreName = (row) =>
    row?.storeName || row?.store?.name || row?.store?.storeName || "";

  const getStoreChildren = (row) => {
    const docs = Array.isArray(row?.documents) ? row.documents : [];
    if (docs.length > 0) return docs;
    return Array.isArray(row?.dataModalRecordsResponse)
      ? row.dataModalRecordsResponse
      : [];
  };

  const selectStoreEntry = (records, codeHint, nameHint) => {
    if (!Array.isArray(records) || records.length === 0) return null;
    const normalizedCode = String(codeHint || "").trim().toLowerCase();
    const normalizedName = String(nameHint || "").trim().toLowerCase();

    if (normalizedCode) {
      const byCode = records.find(
        (entry) => getStoreCode(entry)?.toLowerCase() === normalizedCode
      );
      if (byCode) return byCode;
    }
    if (normalizedName) {
      const byName = records.find(
        (entry) => getStoreName(entry)?.toLowerCase() === normalizedName
      );
      if (byName) return byName;
    }
    return records.find((entry) => getStoreChildren(entry).length > 0) || records[0];
  };

  const mapSubVoucherData = () =>{
    const effectiveStoreName = currentStoreSelect || (storeName !== "All" ? storeName : "");
    const matchedStore = selectStoreEntry(
      storeWiseRecords,
      selectedStoreCode,
      effectiveStoreName
    );
    const currentSubVoucherData = getStoreChildren(matchedStore);
    return (currentSubVoucherData || []).map(
        normalizeVoucherParentRowForStoreColumns
    );
  }

  useEffect(() => {
    if (subVoucherPath === "sub-voucher") {
      setTableData(mapSubVoucherData() || []);
    }
  }, [data, nonProcessedData, subVoucherPath]);

  useEffect(() => {
    let nextData = [];
    if (subVoucherPath === "vouchers") {
      // Prod API returns storeWiseRecordsResponses for Store, not storeGetResponse; boolean processData + storeGetResponse yields undefined/empty.
      if (userLabel === "Store") {
        nextData = storeListRowsFromApi(data);
      } else {
        nextData = storeWiseRecords ?? [];
      }
    } else {
      nextData = mapSubVoucherData() ?? [];
    }
    setTableData(nextData);
  }, [subVoucherPath, data, nonProcessedData, userLabel]);

  /** Avoid `tableData ? tableData` when `tableData === []` (truthy) — use computed rows like Rework tab. */
  const viewAllTableRows = useMemo(() => {
    if (subVoucherPath === "sub-voucher") {
      return Array.isArray(tableData) ? tableData : [];
    }
    if (Array.isArray(tableData) && tableData.length > 0) {
      return tableData;
    }
    if (userLabel === "Store") {
      return storeListRowsFromApi(data);
    }
    return storeWiseRecords ?? [];
  }, [subVoucherPath, tableData, userLabel, data, storeWiseRecords]);

  const voucherApiCall = (start, end) => {
    const safeStart = start || new Date();
    const safeEnd = end || safeStart;
    const storeCodes = getArrayOfSelectedStoresCode(selectedStores, storeDropdownState);
    const requestKey = JSON.stringify({
      startDate: moment(safeStart).startOf("d").format("YYYY-MM-DD HH:mm:ss"),
      endDate: moment(safeEnd).endOf("d").format("YYYY-MM-DD HH:mm:ss"),
      metadataSchemaId: 4,
      status: null,
      storeCodes: Array.isArray(storeCodes) ? [...storeCodes].sort() : [],
    });
    if (
      getRecordsInFlightRef.current &&
      lastFetchKeyRef.current === requestKey
    ) {
      return;
    }
    getRecordsInFlightRef.current = true;
    lastFetchKeyRef.current = requestKey;

    navigate(
      {
        pathname: window.location.pathname,
        search: createSearchParams({
          startDate: moment(safeStart).toISOString(),
          endDate: moment(safeEnd).toISOString(),
        }).toString(),
      },
      { replace: true }
    );
    apiCall({
      url: apiUrls.dmsService.getRecords,
      dataToSend: {
        startDate: moment(safeStart).startOf("d").format("YYYY-MM-DD HH:mm:ss"),
        metadataSchemaId: 4,
        endDate: moment(safeEnd)
          .endOf("d")
          .format("YYYY-MM-DD HH:mm:ss"),
        searchText: "",
        status: null,
        storeCodes,
        // storeCodes: selectedStores?.storeDetails?.storeCode
        //   ? selectedStores?.storeDetails?.storeCode !== "All" ? [selectedStores?.storeDetails?.storeCode] : null
        //   : userLabel == "Store" ? null : [storeCode],
      },
      method: "post",
      onSuccess: () => {
        getRecordsInFlightRef.current = false;
      },
      onFail: () => {
        getRecordsInFlightRef.current = false;
      },
      onError: () => {
        getRecordsInFlightRef.current = false;
      },
      ...(userLabel === "Store"
        ? {
            processData: true,
            accessor: "storeGetResponse",
            itemAccessor: "dataModalRecordsResponse",
          }
        : {}),
    });
  };

  const isStoreActive = userLabel == "Store";

  const isParentPath = location.pathname == "/vouchers";
  useEffect(() => {
    // if(isStoreActive){
    voucherApiCall(startDate, endDate);
    // } else {
    //   if((storeCode.length > 0) && (storeCode.length !== 0)){
    //     voucherApiCall(startDate, endDate);
    //   }
    // }
  }, [storeFetchDependencyKey]);

  const onChangeDate = (dates) => {
    if (Array.isArray(dates)) {
      const [start, end] = dates;
      if (!start) return;

      setStartDate(start);
      setEndDate(end || null);

      // For range picker, trigger API only when end date is selected.
      if (end) {
        voucherApiCall(start, end);
      }
      return;
    }

    if (!dates) return;
    setStartDate(dates);
    setEndDate(dates);
    voucherApiCall(dates, dates);
  };

  const onClickChangeData = (data) => {
    setTableData(data);
  };

  const nonStoreColumns = useMemo(
    () =>
      l1Columns({
        navigate,
        userLabel,
        extraColumns:
          subVoucherPath === "sub-voucher"
            ? [
                {
                  Header: "V.ID",
                  accessor: "id",
                  Cell: (props) => {
                    const id = resolveVoucherListRowId(props?.row?.original);
                    const rowData = props?.row?.original;
                    
                    // Dynamically calculate isPendingDocAvail based on actual data
                    let isPendingDocAvail = false;
                    
                    if (userLabel === "Store") {
                      // Store users can always view their vouchers
                      // Check if there are rework documents that need resubmission
                      isPendingDocAvail = 
                        (rowData?.reworkRequestedDocs?.length > 0) ||
                        (rowData?.reworkPendingDocs?.length > 0) ||
                        (rowData?.pendingDocs?.length > 0) ||
                        (rowData?.pendingAmount > 0);
                    } else {
                      // For L1/L2/Finance users, check if there are pending documents to act on
                      isPendingDocAvail = 
                        (rowData?.pendingDocs?.length > 0) ||
                        (rowData?.pendingApprovedRejectedDocs?.length > 0) ||
                        (rowData?.pendingAmount > 0);
                    }
                    
                      // console.log("🟠 AllVouchers V.ID Cell rendered - id:", id, "isPendingDocAvail:", isPendingDocAvail, "userLabel:", userLabel, "rowData:", props.row?.original);
                    
                    if (!id) {
                      return <span>-</span>;
                    }
                    
                    const handleMouseEnter = (e) => {
                      // console.log("🟢 AllVouchers V.ID MOUSE ENTER - id:", id, "target:", e.target);
                      e.target.style.backgroundColor = "#e3f2fd";
                    };
                    
                    const handleMouseLeave = (e) => {
                      // console.log("🔴 AllVouchers V.ID MOUSE LEAVE - id:", id);
                      e.target.style.backgroundColor = "transparent";
                    };
                    
                    const handleClick = (e) => {
                      // console.log("🖱️ AllVouchers V.ID CLICKED (onClick)! - id:", id, "event:", e);
                      // console.log("🖱️ Click target:", e.target);
                      // console.log("🖱️ Current target:", e.currentTarget);
                      
                      e.preventDefault();
                      e.stopPropagation();
                      if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
                        e.nativeEvent.stopImmediatePropagation();
                      }
                      
                      // console.log("🖱️ After stopPropagation - navigating to /vouchers/" + id);
                      
                      // Always allow navigation if id exists (for viewing details)
                      // isPendingDocAvail only affects visual styling (blue/clickable vs black/non-clickable)
                      if (id) {
                        try {
                          navigate(`/vouchers/${id}`, {
                            state: { ...props.row?.original, type: "All" },
                          });
                          // console.log("✅ AllVouchers Navigation called successfully");
                        } catch (error) {
                          // console.error("❌ AllVouchers Navigation error:", error);
                        }
                      } else {
                        // console.error("❌ Cannot navigate - id is missing");
                      }
                    };
                    
                    const handleMouseDown = (e) => {
                      // console.log("🖱️ AllVouchers V.ID MOUSE DOWN - id:", id, "button:", e.button);
                      // Use mouseDown as primary handler since onClick might be blocked
                      if (e.button === 0) { // Left mouse button
                        e.preventDefault();
                        e.stopPropagation();
                        if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
                          e.nativeEvent.stopImmediatePropagation();
                        }
                        
                        // console.log("🖱️ MouseDown - navigating to /vouchers/" + id);
                        
                        // Always allow navigation if id exists (for viewing details)
                        if (id) {
                          try {
                            navigate(`/vouchers/${id}`, {
                              state: { ...props.row?.original, type: "All" },
                            });
                            // console.log("✅ AllVouchers Navigation called from MouseDown");
                          } catch (error) {
                            // console.error("❌ AllVouchers Navigation error from MouseDown:", error);
                          }
                        }
                      }
                    };
                    
                    return (
                      <div
                        className={`${
                          isPendingDocAvail
                            ? "underline underline-offset-4 cursor-pointer"
                            : ""
                        }`}
                        style={{ 
                          color: isPendingDocAvail ? "blue" : "black",
                          cursor: isPendingDocAvail ? "pointer" : "default",
                          pointerEvents: "auto",
                          zIndex: 9999,
                          position: "relative",
                          userSelect: "none",
                          WebkitUserSelect: "none"
                        }}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        onClick={handleClick}
                        onMouseDown={handleMouseDown}
                        onMouseUp={(e) => {
                          // console.log("🖱️ AllVouchers V.ID MOUSE UP - id:", id, "button:", e.button);
                        }}
                        onContextMenu={(e) => {
                          // console.log("🖱️ AllVouchers V.ID RIGHT CLICK - id:", id);
                          e.preventDefault(); // Prevent right-click menu
                        }}
                      >
                        {id}
                      </div>
                    );
                  },
                },
              ]
            : [
                {
                  Header: "Store Name",
                  accessor: "storeName",
                  Cell: (props) => {
                    const store_name = props?.row?.original?.storeName;
                    return (
                      <div
                        className="cursor-pointer underline underline-offset-4"
                        style={{ color: "blue" }}
                        onClick={() => {
                          navigate(
                            {
                              pathname: "sub-voucher",
                              search: createSearchParams({
                                startDate: moment(startDate).toISOString(),
                                endDate: moment(endDate).toISOString(),
                              }).toString(),
                            }
                          );
                          setCurrentStoreSelect(store_name);
                          return onClickChangeData(
                            getStoreChildren(props?.row?.original).map(
                              normalizeVoucherParentRowForStoreColumns
                            )
                          );
                        }}
                      >
                        {store_name}
                      </div>
                    );
                  },
                },
              ],
      }),
    // userLabel and subVoucherPath must be deps: l1Columns branches Approved column by role; without them
    // the memo can stick to the pre-auth or wrong-role cell logic after useAuth populates the real label.
    [tableData, startDate, endDate, userLabel, subVoucherPath, navigate]
  );

  const onClickDownloadFile = () => {
    // const st =`${moment(startDate).format('YYYY-MM-DD')} 00:00:00`;
    // const et = `${moment(endDate).format('YYYY-MM-DD')} 23:59:59`;
    fileDownload({
      url: `${apiUrls.dmsService.getDownloadVoucherReport}`,
      // dataToSend: userLabel == "Store" ? selectedCheckBoxes?.map(i => Number(i.original.vid)):  selectedCheckBoxes?.map(i => Number(i.original.id)) || [],
      dataToSend: {
        voucherIds:
          userLabel === "Store"
            ? selectedCheckBoxes?.map((i) => Number(i.original.vid))
            : selectedCheckBoxes?.map((i) => Number(i.original.id)) || [],
        status: "All",
      },
      method: "post",
      responseType: "blob",
      responseData: (res) => {
        const { data = "", status = null } = res || {};
        const contentType = res?.headers?.["content-type"];
        const contentDisposition = res?.headers["content-disposition"];
        const fileNameMatch =
          contentDisposition && contentDisposition.match(/filename="(.+)"/);
        const fileName = fileNameMatch
          ? fileNameMatch[1]
          : "downloaded-file.xlsx";
        if (status === 200) {
          toast.success("Download File Successfully");
          downloadReportsFun(data, "AllVoucher.xlsx", contentType);
        } else {
          toast.success("Something Went Wrong");
        }
      },
    });
  };

  const getStoreCodeByName = (storeName) => {
    return storeDropdownState.find((i) => i.name == storeName)?.storeCode;
  };

  const downloadVoucherAtStoreLevel = () => {
    const st = `${moment(startDate).format("YYYY-MM-DD")} 00:00:00`;
    const et = `${moment(endDate).format("YYYY-MM-DD")} 23:59:59`;
    fileDownloadAtStore({
      url: `${apiUrls.dmsService.downloadVoucherStoreLevel}`,
      dataToSend: {
        storeCodes: selectedCheckBoxes?.map((i) =>
          getStoreCodeByName(i.original.storeName)
        ),
        status: "All",
        metadataSchemaId: 4,
        endDate: et,
        startDate: st,
      },
      method: "post",
      responseType: "blob",
      responseData: (res) => {
        const { data = "", status = null } = res || {};
        const contentType = res?.headers?.["content-type"];
        const contentDisposition = res?.headers["content-disposition"];
        const fileNameMatch =
          contentDisposition && contentDisposition.match(/filename="(.+)"/);
        const fileName = fileNameMatch
          ? fileNameMatch[1]
          : "downloaded-file.xlsx";
        if (status === 200) {
          toast.success("Download File Successfully");
          downloadReportsFun(data, "AllVoucher.xlsx", contentType);
        } else {
          toast.success("Something Went Wrong");
        }
      },
    });
  };

  const memoStoreColumns = useMemo(() => storeColumns({ navigate }), []);

  return (
    <div className="flex flex-col py-2">
      <div className="flex justify-end mt-2 mb-8">
        {/* <InputWithIcon
          placeholder="Search"
          containerClassName="search-input-wrapper"
          inputClassName="font-size-small"
          rightIcon={icons.searchIcon}
          onChange={(e) => {
            setSearchText(e);
          }}
        /> */}
        
        <div className="flex items-center gap-x-3">
          <div className="font-size-medium">Date Range</div>
          <DatePicker
            startDate={startDate}
            endDate={endDate}
            onChange={onChangeDate}
            maxDate={new Date()}
            dateRange
          />
        </div>
      </div>
      <CustomTable
        data={viewAllTableRows}
        columns={userLabel === "Store" ? memoStoreColumns : nonStoreColumns}
        showCheckboxes={false}
        onCheckboxSelect={(selectedCheckBoxes) => {
          if (viewAllTableRows?.length) {
            setSelectedCheckBoxes(selectedCheckBoxes);
          }
        }}
        centerAlignedColumns={[
          "l1_status",
          "l2_status",
          "amount",
          "ac_status",
          "approved_amount",
        ]}
        // isGlobalSearchEnable
        // globalSearchPosition={{
        //   top: "-63px",
        //   left: 0,
        // }}
      />

      {/* <CustomButton
        title="Download"
        wrapperClassName={`self-end !px-10 mt-5 ${
          selectedCheckBoxes?.length > 0 ? "" : "disabled-kv"
        }`}
        onClick={() => {
          if(selectedCheckBoxes?.map((item) => item?.original)[0]?.storeName){
            downloadVoucherAtStoreLevel()
          } else {
            onClickDownloadFile()
          }
        }}
      /> */}
    </div>
  );
};

export default AllVouchers;
